import type {Router, Params} from 'src/types/router';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {usePageTitle} from 'src/contexts/page-title-context';
import type {Tearsheet, TearsheetSync} from 'src/types/ats-settings';
// @ts-expect-error - TS2614 - Module '"src/components/lib/table"' has no exported member 'TableHeader'. Did you mean to use 'import TableHeader from "src/components/lib/table"' instead?
import type {TableHeader} from 'src/components/lib/table';

import * as React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Helmet} from 'react-helmet';
import capitalize from 'lodash/capitalize';
import pickBy from 'lodash/pickBy';

import {emptyArray} from 'src/utils';
import * as api from 'src/utils/api-no-store';
import * as reduxApi from 'src/utils/redux-api-v2';
import {formatDateWithLocale} from 'src/utils/date-time-2';

import {pushModal, popModal} from 'src/action-creators/modal';

import MouseTip from 'src/components/lib/mouse-tip';
import {TitleBarModal} from 'src/components/modals/base-modal';
import {selectEntityMappingsByName} from 'src/selectors/ats-entities';
import {selectAllowedTearsheetTypes} from 'src/selectors/ats-settings';

import Table, {
  TablePage,
  TableContainer,
  TablePageHeader,
  Cells,
  // Cell,
} from 'src/components/lib/table';
import PaginationFooter from 'src/components/lib/table/pagination';
import Loading from 'src/components/lib/loading';
import Warning from 'src/components/lib/warning';
import Tabs from 'src/components/lib/tabs';
import Checkbox from 'src/components/lib/checkbox';
import {PageName} from '@spaced-out/ui-design-system/lib/components/PageTitle';
import {TitleMedium} from '@spaced-out/ui-design-system/lib/components/Text';

import css from 'src/components/settings/ats/ats.css';


const initialState = {
  isLoading: false,
  rows: null,
  total: -1,
  searchText: '',
  warningMessage: null,
} as const;

// @ts-expect-error - TS7006 - Parameter 'state' implicitly has an 'any' type. | TS7006 - Parameter 'action' implicitly has an 'any' type.
function reducer(state, action) {
  switch (action.type) {
    case 'startFetching':
      return {
        ...state,
        isLoading: true,
        warningMessage: null,
      };
    case 'receiveTearsheets':
      return {
        ...state,
        ...action.payload,
        warningMessage: null,
        isLoading: false,
      };
    case 'receiveBullhornError':
      return {
        ...state,
        warningMessage:
          'Something went wrong requesting your data from Bullhorn. Please try again later.',
        isLoading: false,
      };
    case 'updateSearchText':
      return {
        ...state,
        searchText: action.payload,
      };
    case 'updateOneTearsheet':
      return {
        ...state,
        warningMessage:
          'Tearsheet is syncing. Come back later to check on its progress.',

        // @ts-expect-error - TS7006 - Parameter 'sheet' implicitly has an 'any' type.
        rows: state.rows?.map((sheet) => {
          if (sheet.external_source_id === action.payload.external_source_id) {
            return {...sheet, tearsheet_sync: action.payload};
          }
          return sheet;
        }),
      };
    default:
      throw new Error();
  }
}

const Tearsheets = ({
  router,

  router: {
    location: {
      query: {page: queryPage},
    },
  },

  params: {syncStatus: querySyncStatus},
}: {
  router: Router;
  params: Params;
}): React.ReactElement<React.ComponentProps<'div'>> => {
  const {t, language} = useLabelTranslation();
  const {
    actions: {setPageTitleProps},
  } = usePageTitle();
  const tabs = [
    {
      url: '',
      name: t('All', 'All'),
      onlyActiveOnIndex: true,
      nameOnServer: [''],
    },
    {
      url: '/scheduled',
      name: t('In_Progress', 'In Progress'),
      nameOnServer: ['not_synced', 'processing'],
    },
    {
      url: '/synced',
      name: t('Synced', 'Synced'),
      nameOnServer: ['synced'],
    },
  ];
  const tearsheetHeader: TableHeader = [
    {
      key: 'external_source_id',
      label: `${t('ID', 'ID')}`,
      className: css.tearsheetCell,
    },
    {
      key: 'name',
      label: `${t('Name', 'Name')}`,
      className: css.tearsheetCell,
    },
    {
      key: 'date_added',
      label: `${t('Date_Added', 'Date Added')}`,
      className: css.tearsheetCell,
    },
    {
      key: 'date_modified',
      label: `${t('Date_Modified', 'Date Modified')}`,
      className: css.tearsheetCell,
    },
    {
      key: 'tearsheet_sync',
      label: `${t('Status_Actions', 'Status / Actions')}`,
      className: css.tearsheetCell,
    },
  ];
  const tearsheetSyncHeader: TableHeader = [
    {
      key: 'external_source_id',
      label: `${t('ID', 'ID')}`,
      className: css.tearsheetCell,
    },
    {
      key: 'name',
      label: `${t('Name', 'Name')}`,
      className: css.tearsheetCell,
    },
    {
      key: 'ats_entity_type',
      label: `${t('Entity_Type', 'Entity Type')}`,
      className: css.tearsheetCell,
    },
  ];
  const dispatch = useDispatch();
  const [searchInputValue, setSearchInputValue] = React.useState('');
  const [{rows, total, warningMessage, searchText, isLoading}, localDispatch] =
    React.useReducer(reducer, initialState);

  const syncStatus = querySyncStatus ?? null;
  const page = parseInt(queryPage) || 1;
  const limit = 10;
  const query = {
    limit,
    offset: (page - 1) * limit,
    id_or_name: searchText.length ? searchText : null,
    sync_status: syncStatus
      ? tabs
          .find(({url}) => url.includes(String(syncStatus)))
          ?.nameOnServer.join(',')
      : null,
  } as const;
  let maxPages = 0;
  if (total > 0) {
    maxPages = Math.ceil(total / query.limit);
  }
  const queryVals = React.useMemo(() => Object.values(query), [query]);

  React.useEffect(() => {
    localDispatch({
      type: 'startFetching',
    });
    api
      .get(
        `tearsheets${syncStatus ? '/syncs' : ''}`,
        pickBy(query, (val) => val !== null) || {},
      )
      .then(({tearsheets, tearsheet_syncs, total}) => {
        const rows: Tearsheet | TearsheetSync[] = tearsheets || tearsheet_syncs;
        if (rows) {
          localDispatch({
            type: 'receiveTearsheets',
            payload: {
              rows,
              total,
            },
          });
        }
      })
      .catch(() => {
        localDispatch({
          type: 'receiveBullhornError',
        });
      });
  }, queryVals);

  React.useEffect(() => {
    setPageTitleProps({
      customPageNameComponent: (
        <PageName>
          <TitleMedium>{`${t('Tearsheets', 'Tearsheets')}`}</TitleMedium>
        </PageName>
      ),
      showBackButton: true,
      onBack: () => {
        router.replace('/settings');
      },
    });
  }, [language]);

  return (
    <div className={css.tearsheetsContainer}>
      <Tabs tabs={tabs} baseUrl="/settings/ats/tearsheets" />
      <div className={css.contentContainer}>
        {/* @ts-expect-error TS2746 */}
        <TablePage>
          {/* @ts-expect-error - TS2786 - 'Helmet' cannot be used as a JSX component. */}
          <Helmet title={t('ATS_Tearsheets', 'ATS Tearsheets')} />
          {/* @ts-expect-error TS2769 */}
          <TablePageHeader className={css.tearsheetsHeader}>
            <form
              className={css.tearsheetFilters}
              onSubmit={(event) => {
                event.preventDefault();
                localDispatch({
                  type: 'updateSearchText',
                  payload: searchInputValue,
                });
                router.replace({...router.location, query: {}});
              }}
            >
              <input
                className={css.tearsheetSearchInput}
                type="text"
                value={searchInputValue}
                placeholder={`${t(
                  'Filter_by_name_or_ID',
                  'Filter by name or ID',
                )}`}
                onChange={(event) => {
                  setSearchInputValue(event.target.value);
                }}
              />
              <button
                className={css.tearsheetFilterButton}
                disabled={searchInputValue === searchText}
              >{`${t('Filter', 'Filter')}`}</button>
              <button
                className={css.tearsheetFilterButton}
                disabled={
                  !searchInputValue.length || !searchInputValue === searchText
                }
                onClick={() => {
                  setSearchInputValue('');
                  localDispatch({
                    type: 'updateSearchText',
                    payload: '',
                  });
                }}
              >{`${t('Clear', 'Clear')}`}</button>
            </form>
          </TablePageHeader>
          {warningMessage && (
            <Warning className={css.postUpdateMessage}>
              {warningMessage}
            </Warning>
          )}
          {/* @ts-expect-error TS2746 */}
          <TableContainer>
            <Table
              className={css.table}
              header={syncStatus ? tearsheetSyncHeader : tearsheetHeader}
              entries={rows || emptyArray}
              // @ts-expect-error - TS2769 - No overload matches this call.
              Row={syncStatus ? TearsheetSyncRow : TearsheetRow}
              extras={{
                // @ts-expect-error - TS7006 - Parameter 'tearsheetData' implicitly has an 'any' type.
                openModal: (tearsheetData) =>
                  dispatch(
                    pushModal({
                      type: 'TEARSHEET_TYPE_SELECT',
                      // @ts-expect-error - TS2353 - Object literal may only specify known properties, and 'tearsheetData' does not exist in type 'BasicModal'.
                      tearsheetData,
                      // @ts-expect-error - TS7006 - Parameter 'rows' implicitly has an 'any' type.
                      updateOneTearsheet: (rows) =>
                        localDispatch({
                          type: 'updateOneTearsheet',
                          payload: rows,
                        }),
                    }),
                  ),
              }}
              isLoading={isLoading}
            />
            {maxPages > 1 && (
              <PaginationFooter
                page={page}
                maxPages={maxPages}
                url={
                  syncStatus
                    ? `/settings/ats/tearsheets/${syncStatus}`
                    : '/settings/ats/tearsheets'
                }
                router={router}
              />
            )}
          </TableContainer>
        </TablePage>
      </div>
    </div>
  );
};

export default Tearsheets;

const TearsheetRow = ({
  data,

  data: {
    name,
    external_source_id,
    date_added,
    date_modified,
    tearsheet_sync,
    is_in_config,
  },

  extras: {openModal},
}: {
  data: Tearsheet;
  extras: {
    openModal: (arg1: Tearsheet) => void;
  };
}) => {
  const {t, language} = useLabelTranslation();
  const getLastColumnContent = () => {
    let status = tearsheet_sync?.sync_status;
    if (is_in_config) {
      // @ts-expect-error - TS2322 - Type '"legacy"' is not assignable to type 'TearsheetSyncStatus | undefined'.
      status = 'legacy';
    }
    switch (status) {
      // @ts-expect-error - TS2678 - Type '"legacy"' is not comparable to type 'TearsheetSyncStatus | undefined'.
      case 'legacy':
      case 'synced':
        return t('Synced', 'Synced');
      case 'not_synced':
      case 'processing':
        return t('In_Progress', 'In Progress');
      case 'error':
        return (
          <span className={css.syncError}>{`${t('Error', 'Error')}`}</span>
        );
      case null:
      default:
        const MAX_MEMBERS = 10000;
        let error_reason = null;

        if (
          data.total_candidates > MAX_MEMBERS ||
          data.total_contacts > MAX_MEMBERS
        ) {
          error_reason = `${t(
            'Tearsheets_with_more_than',
            'Tearsheets with more than',
          )} ${MAX_MEMBERS} ${t(
            'members_cannot_be_synced',
            'members cannot be synced',
          )}. ${t(
            'Consider_breaking_this_up_into_multiple_tearsheets',
            'Consider breaking this up into multiple tearsheets',
          )}.`;
        }

        return error_reason ? (
          <MouseTip content={error_reason}>
            {/* @ts-expect-error - TS2322 - Type '{ children: string; className: string; disabled: true; }' is not assignable to type 'DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>'. */}
            <div className={css.noSyncAllowed} disabled>{`${t(
              'Cannot_sync',
              'Cannot sync',
            )}`}</div>
          </MouseTip>
        ) : (
          <button
            className={css.syncButton}
            onClick={() => openModal(data)}
          >{`${t('Sync', 'Sync')}`}</button>
        );
    }
  };

  const localizedDateAdded = formatDateWithLocale(date_added, language, 'L');
  const localizedDateModified = formatDateWithLocale(
    date_modified,
    language,
    'L',
  );

  return (
    <Cells key={external_source_id}>
      <div className={css.tearsheetCell}>{external_source_id}</div>
      <div className={css.tearsheetCell}>{name}</div>
      <div className={css.tearsheetCell}>{localizedDateAdded}</div>
      <div className={css.tearsheetCell}>{localizedDateModified}</div>
      <div className={css.tearsheetCell}>{getLastColumnContent()}</div>
    </Cells>
  );
};

export const TearsheetTypeSelectModal = ({
  tearsheetData,
  updateOneTearsheet,
}: {
  tearsheetData: Tearsheet;
  updateOneTearsheet: (arg1: TearsheetSync) => void;
}) => {
  const {t} = useLabelTranslation();

  // Selectors
  const allowedTearsheetTypes = useSelector(selectAllowedTearsheetTypes);
  const entityMappings = useSelector(selectEntityMappingsByName);

  // Dispatch
  const dispatch = useDispatch();
  const closeModal = () => dispatch(popModal());

  // @ts-expect-error - TS18049 - 'allowedTearsheetTypes' is possibly 'null' or 'undefined'.
  const options = allowedTearsheetTypes.map((tearsheetType) => {
    const targetEntityMapping = entityMappings[tearsheetType];
    return {
      // @ts-expect-error - TS18048 - 'targetEntityMapping' is possibly 'undefined'.
      value: targetEntityMapping.name,
      // @ts-expect-error - TS18048 - 'targetEntityMapping' is possibly 'undefined'.
      label: capitalize(targetEntityMapping.display_name),
    };
  });

  const [syncEntityTypes, setEntityTypeOptions] = React.useState(
    options.map((option) => ({...option, checked: false})),
  );
  const checkEntityType = (entityType: string, checked: boolean) => {
    setEntityTypeOptions((options) =>
      options.map((opt) => {
        if (opt.value === entityType) {
          return {...opt, checked};
        } else {
          return opt;
        }
      }),
    );
  };
  const handleEntityChecked = React.useCallback(
    (entityType: string) => (evt: React.SyntheticEvent<HTMLInputElement>) =>
      checkEntityType(entityType, evt.currentTarget.checked),
    [syncEntityTypes],
  );

  const handleSync = React.useCallback(async () => {
    const response = await Promise.all(
      syncEntityTypes
        .filter(({checked}) => checked)
        .map(async ({value}) =>
          dispatch(
            reduxApi.post('tearsheets', {
              name: tearsheetData.name,
              external_source_id: tearsheetData.external_source_id,
              entity_type: value,
            }),
          ),
        ),
    );
    // @ts-expect-error - TS2345 - Argument of type 'ThunkAction<any>[]' is not assignable to parameter of type 'TearsheetSync'.
    updateOneTearsheet(response);
  }, [syncEntityTypes]);

  if (allowedTearsheetTypes === null) {
    return <Loading />;
  }

  return (
    <TitleBarModal
      type="TEARSHEET_TYPE_SELECT"
      title={t(
        'Choose_entity_types_for_this_tearsheet',
        'Choose entity types for this tearsheet',
      )}
      handleAbort={closeModal}
      abortText={t('Cancel', 'Cancel')}
      handleConfirm={() => handleSync()}
      confirmText="Sync"
      removeModal={closeModal}
    >
      <p>{`${t(
        'This_tearsheet_may_contain_records_for_both_candidate_and_contact_entity_types',
        'This tearsheet may contain records for both candidate and contact entity types',
      )}. ${t(
        'Choose_if_you_want_to_use_only_one_or_both_of_these_entity_types_within_your_journeys_For_example_if_you_only_check_mark_candidates_then_any_records_for_contacts_in_this_tearsheet_will_be_ignored_and_wont_be_available_to_your_journeys',
        'Choose if you want to use only one or both of these entity types within your journeys. For example, if you only check mark “candidates”, then any records for “contacts” in this tearsheet will be ignored and won’t be available to your journeys',
      )}.`}</p>
      <form>
        <div className={css.syncEntityChoiceBox}>
          <h5>{`${t(
            'Check_one_or_more_entity_types',
            'Check one or more entity types',
          )}`}</h5>
          {syncEntityTypes.map(({label, value, checked}) => (
            <label>
              <Checkbox
                checked={checked}
                name={value}
                onChange={handleEntityChecked(value)}
                className={css.entityCheckbox}
              />{' '}
              {label}
            </label>
          ))}
        </div>
      </form>
    </TitleBarModal>
  );
};

const TearsheetSyncRow = ({
  data: {name, external_source_id, ats_entity_type},
}: {
  data: TearsheetSync;
}) => {
  const {t, makeKey} = useLabelTranslation();
  return (
    <Cells key={external_source_id}>
      <div className={css.tearsheetCell}>{external_source_id}</div>
      <div className={css.tearsheetCell}>{name}</div>
      <div className={css.tearsheetCell}>
        {t(makeKey(ats_entity_type), ats_entity_type)}
      </div>
    </Cells>
  );
};
